<ngx-spinner color="#cd0005"></ngx-spinner>

<!-- Reports View -->
<div *ngIf="loaded" id="reports-view" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-[1024px]">
  <div class="mb-8">
    <h1 class="text-2xl font-semibold text-gray-900">Booking Reports</h1>
    <p class="mt-2 text-sm text-gray-600">
      View and analyze booking volumes across different dimensions
    </p>
  </div>
  <div class="bg-white rounded-lg shadow mb-6">
    <div class="grid md:grid-cols-3 gap-4 p-4">
      <div class="form-control w-full">
        <label class="label">
          <span class="label-text">Select Date Range</span>
        </label>
        <input
          type="text"
          ngxDaterangepickerMd
          [(ngModel)]="selectedDateRange"
          (datesUpdated)="onDateRangeChange($event)"
          [showCustomRangeLabel]="true"
          [locale]="{ applyLabel: 'OK', format: 'YYYY-MM-DD' }"
          [ranges]="ranges"
          [timePicker]="false"
          class="input w-full rounded border-gray-300 shadow-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
          placeholder="Select date range"
        />
      </div>
      <div class="flex flex-col justify-end w-full md:w-auto">
        <label class="label invisible"></label>
        <div
          class="dropdown dropdown-end w-full md:w-auto mr-auto"
          [class.dropdown-open]="isExportDropdownOpen"
        >
          <button
            type="button"
            class="btn rounded inline-flex items-center justify-start w-full px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            (click)="isExportDropdownOpen = !isExportDropdownOpen"
          >
            <i class="ri-download-line mr-1"></i>
            Export
          </button>
          <ul
            *ngIf="isExportDropdownOpen"
            class="dropdown-content menu p-2 shadow bg-white rounded-box w-full mt-1 min-w-max max-h-[300px] overflow-auto"
            (click)="$event.stopPropagation()"
          >
            <form [formGroup]="emailForm">
              <div class="mb-4 space-y-2">
                <div *ngFor="let email of recipientEmails; last as last">
                  <label
                    class="flex items-center gap-2 cursor-pointer"
                    [class.mb-0]="last"
                    [for]="email"
                  >
                    <input
                      type="checkbox"
                      class="checkbox checkbox-sm"
                      [id]="email"
                      (change)="onPassengerEmailsChange(email)"
                      [checked]="selectedRecipientEmails.includes(email)"
                    />
                    <span class="text-sm lowercase">{{ email }}</span>
                  </label>
                </div>
              </div>
              <div class="flex items-center gap-2 mb-4">
                <input
                  class="input input-sm input-bordered border-gray-300 shadow-sm focus:outline-none"
                  type="text"
                  placeholder="web@example.com"
                  formControlName="email"
                  (click)="$event.stopPropagation()"
                />
                <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  (click)="addEmail(); $event.stopPropagation()"
                  [disabled]="emailForm.get('email')?.invalid"
                >
                  <i class="ri-add-line"></i>
                </button>
              </div>

              <button
                type="button"
                class="btn btn-primary w-full"
                (click)="export()"
                [disabled]="!selectedRecipientEmails.length"
              >
                Send
              </button>
            </form>
          </ul>
        </div>
      </div>
    </div>
    <div class="grid md:grid-cols-3 gap-4 px-4 pb-4">
      <div class="dropdown relative w-full">
        <label class="label">
          <span class="label-text">Group by</span>
        </label>

        <button
          tabindex="0"
          type="button"
          class="btn w-full rounded font-medium text-gray-700 bg-white border border-gray-300 shadow-sm hover:bg-gray-50 inline-flex items-center justify-between"
        >
          {{ selectedRange | titlecase }}
          <i class="ri-arrow-down-s-line ml-2"></i>
        </button>

        <ul
          tabindex="0"
          class="dropdown-content menu p-2 shadow bg-white rounded-box w-full absolute z-10 mt-1"
        >
          <li><a (click)="selectRange('day')">Day</a></li>
          <li><a (click)="selectRange('week')">Week</a></li>
          <li><a (click)="selectRange('month')">Month</a></li>
          <li><a (click)="selectRange('year')">Year</a></li>
        </ul>
      </div>
      <div class="dropdown relative w-full" *ngIf="agencies.length">
        <label class="label">
          <span class="label-text">Agencies</span>
        </label>

        <button
          tabindex="0"
          type="button"
          class="btn w-full rounded font-medium text-gray-700 bg-white border border-gray-300 shadow-sm hover:bg-gray-50 inline-flex items-center justify-between"
        >
          {{ selectedAgency?.name || 'All' }}
          <i class="ri-arrow-down-s-line ml-2"></i>
        </button>

        <ul
          tabindex="0"
          class="dropdown-content flex-nowrap menu p-2 shadow bg-white rounded-box w-full absolute z-10 mt-1 max-h-[400px] overflow-auto"
        >
          <li *ngIf="selectedAgency"><a (click)="selectAgency(null)">All</a></li>
          <li *ngFor="let agency of agencies">
            <a (click)="selectAgency(agency)">{{ agency.name }}</a>
          </li>
        </ul>
      </div>
      <div class="dropdown relative w-full">
        <label class="label">
          <span class="label-text">Listing by</span>
        </label>

        <button
          tabindex="0"
          class="btn w-full rounded font-medium text-gray-700 bg-white border border-gray-300 shadow-sm hover:bg-gray-50 inline-flex items-center justify-between"
        >
          <span id="selectedGroup" class="ml-1">{{ selectedGroup | titlecase }}</span>
          <i class="ri-arrow-down-s-line ml-2"></i>
        </button>
        <ul tabindex="0" class="dropdown-content menu p-2 shadow bg-white rounded-box w-40">
          <li><a (click)="selectGroup('provider')">Provider</a></li>
          <li><a (click)="selectGroup('agent')">Agent</a></li>
          <li><a (click)="selectGroup('application')">Application</a></li>
          <li><a (click)="selectGroup('agency')">Agency</a></li>
        </ul>
      </div>
    </div>
    <div class="overflow-x-auto">
      <div *ngFor="let group of statsSummaryDataPerApp; let idx = index; first as first">
        <h4
          class="text-xl font-bold px-6 py-2 mb-1 bg-gray-100 shadow-sm"
          [class.mt-5]="!first"
          *ngIf="selectedGroup !== 'application'"
        >
          <ng-container *ngIf="group[0].application; else noApp">
            Application: {{ group[0].application.name || 'N/A' }}
          </ng-container>
          <ng-template #noApp>Application: N/A</ng-template>
        </h4>

        <table
          class="min-w-full divide-y divide-gray-200 table-fixed"
          [class.mt-5]="!first && selectedGroup === 'application'"
        >
          <thead class="bg-gray-50">
            <tr>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Name
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Booked
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Issued
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Voided
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Expired
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Cancelled
              </th>
            </tr>
          </thead>

          <tbody class="bg-white divide-y divide-gray-200">
            <tr *ngFor="let stat of group">
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                <ng-container *ngIf="selectedGroup === 'provider'">
                  {{ stat.provider?.agw_id || 'N/A' }}
                </ng-container>
                <ng-container *ngIf="selectedGroup === 'agent'">
                  {{ stat.account?.email || 'N/A' }}
                </ng-container>
                <ng-container *ngIf="selectedGroup === 'application'">
                  {{ stat.application?.name || 'N/A' }}
                </ng-container>
                <ng-container *ngIf="selectedGroup === 'agency'">
                  {{ stat.agency?.name || 'N/A' }}
                </ng-container>
              </td>

              <td class="px-6 py-4 text-sm text-gray-500">{{ stat.orders_created }}</td>
              <td class="px-6 py-4 text-sm text-gray-500">{{ stat.orders_issued }}</td>
              <td class="px-6 py-4 text-sm text-gray-500">{{ stat.orders_voided }}</td>
              <td class="px-6 py-4 text-sm text-gray-500">{{ stat.orders_expired }}</td>
              <td class="px-6 py-4 text-sm text-gray-500">{{ stat.orders_cancelled }}</td>
            </tr>

            <tr
              class="bg-gray-50 font-semibold"
              *ngIf="group.length > 1 && totalStatsSummaryPerApp[idx]"
            >
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800 uppercase">Total</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                {{ totalStatsSummaryPerApp[idx]['orders_created'] }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                {{ totalStatsSummaryPerApp[idx]['orders_issued'] }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                {{ totalStatsSummaryPerApp[idx]['orders_voided'] }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                {{ totalStatsSummaryPerApp[idx]['orders_expired'] }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                {{ totalStatsSummaryPerApp[idx]['orders_cancelled'] }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div *ngIf="!statsSummaryDataPerApp?.length" class="text-center py-4 text-gray-500">
        No data available
      </div>
    </div>
  </div>
  <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
    <div class="bg-white rounded-lg shadow p-6">
      <h3 class="text-lg font-medium text-gray-900 mb-4">Booking Status Distribution</h3>
      <div class="flex justify-center">
        <canvas
          baseChart
          [labels]="doughnutChartLabels"
          [data]="doughnutChartData"
          [options]="doughnutChartOptions"
          [legend]="true"
          [type]="'doughnut'"
          [height]="300"
        ></canvas>
      </div>
    </div>
    <div class="bg-white rounded-lg shadow p-6">
      <h3 class="text-lg font-medium text-gray-900 mb-4">Booking Trends</h3>
      <canvas
        baseChart
        [data]="lineChartData"
        [options]="lineChartOptions"
        [type]="'line'"
        [plugins]="[verticalDashedLinePlugin]"
      ></canvas>
    </div>
  </div>
</div>
